import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Check, FileSpreadsheet, X, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";

import Modal from "../../../shared/components/ui/Modal";

import { useSensorTypes } from "../components/calibrationModal/hooks/useSensorTypes";
import { useCalibrationForm } from "../components/calibrationModal/hooks/useCalibrationForm";
import { useBulkUpload } from "../components/calibrationModal/hooks/useBulkUpload";
import { CalibrationService } from "../services/calibration.service";
import { buildGeneratedSampleCsv, downloadCsvTemplate } from "../components/calibrationModal/utils/bulkUpload";
import { isDisplacementCategory, rowBelongsToFamily } from "../utils/displacementModelFamily";
import { matchesCategory } from "../sensors/categoryUtils";

import { SensorFiltersStep } from "../bulkCalibration/steps/SensorFiltersStep";
import { UploadCsvStep } from "../bulkCalibration/steps/UploadCsvStep";
import { BulkPreviewStep } from "../bulkCalibration/steps/BulkPreviewStep";

const STEPS = ["Sensor Filters", "Upload CSV", "Preview & Confirm"];


interface ValidationError {
  type?: string;
  loc?: (string | number)[];
  msg?: string;
  message?: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
  url?: string;
}

interface SensorColumnError {
  serial_number?: string;
  column?: number;
  reason?: string;
}

interface ErrorDetail {
  message?: string;
  error_details?: string;
  error?: string;
  expected?: unknown[];
  found?: unknown[];
  errors?: SensorColumnError[];
}

interface ApiErrorResponse {
  detail?: string | ValidationError[] | ErrorDetail;
}

const getErrorMessage = (error: unknown): string => {
  const apiError = error as {
    response?: {
      data?: ApiErrorResponse;
    };
    message?: string;
  };

  const detail = apiError.response?.data?.detail;

  // 1. detail is a plain string
  if (typeof detail === "string") {
    return detail;
  }

  // 2. detail is an array of FastAPI/Pydantic validation errors
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        const message = item?.msg || item?.message;

        if (!message) {
          return null;
        }

        const loc = item?.loc ?? [];
        const field = loc[loc.length - 1];

        return field && field !== "body"
          ? `${String(field)}: ${message}`
          : message;
      })
      .filter((message): message is string => Boolean(message))
      .join("\n");
  }

  // 3. detail is an object
  if (detail && typeof detail === "object") {
    const message =
      detail.message ||
      detail.error_details ||
      detail.error;

    if (message) {
      const lines: string[] = [message];

      // Include expected values
      if (Array.isArray(detail.expected)) {
        lines.push(`Expected: ${detail.expected.join(", ")}`);
      }

      // Include found values
      if (Array.isArray(detail.found)) {
        lines.push(`Found: ${detail.found.join(", ")}`);
      }

      // Include sensor column errors
      if (Array.isArray(detail.errors) && detail.errors.length > 0) {
        lines.push("");

        detail.errors.forEach((item) => {
          const serial = item.serial_number
            ? `Sensor ${item.serial_number}`
            : "Sensor";

          const column =
            item.column !== undefined
              ? ` (Column ${item.column})`
              : "";

          const reason = item.reason
            ? `: ${item.reason}`
            : "";

          lines.push(`${serial}${column}${reason}`);
        });
      }

      return lines.join("\n");
    }
  }

  // 4. Fallback
  return apiError.message || "Failed to process bulk file.";
};


const BulkCalibrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [processError, setProcessError] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const errorBannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (processError) {
      errorBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [processError]);

  const sensorTypes = useSensorTypes(true);
  const { updateCategories, updateModelFamilies, updateModels, updateRanges } = sensorTypes;
  const { individualCalibrationData, setIndividualCalibrationData, schemaConfig } = useCalibrationForm(
    undefined,
    undefined,
    true
  );

  const bulkUpload = useBulkUpload();

  useEffect(() => {
    if (individualCalibrationData.sensorType) {
      updateCategories(individualCalibrationData.sensorType);
    }
  }, [individualCalibrationData.sensorType, updateCategories]);

  useEffect(() => {
    if (individualCalibrationData.sensorType && individualCalibrationData.category) {
      const category = individualCalibrationData.category;
      if ((category || "").toLowerCase() === "displacement") {
        updateModelFamilies(individualCalibrationData.sensorType, category);
        if (individualCalibrationData.modelFamily) {
          updateModels(individualCalibrationData.sensorType, category, individualCalibrationData.modelFamily);
        }
      } else {
        updateModels(individualCalibrationData.sensorType, category);
      }
    }
  }, [
    individualCalibrationData.sensorType,
    individualCalibrationData.category,
    individualCalibrationData.modelFamily,
    updateModelFamilies,
    updateModels,
  ]);

  useEffect(() => {
    if (
      individualCalibrationData.sensorType &&
      individualCalibrationData.category &&
      individualCalibrationData.model
    ) {
      updateRanges(
        individualCalibrationData.sensorType,
        individualCalibrationData.category,
        individualCalibrationData.model
      );
    }
  }, [
    individualCalibrationData.sensorType,
    individualCalibrationData.category,
    individualCalibrationData.model,
    updateRanges,
  ]);

  const selectedBulkSensorType = useMemo(() => {
    const range = parseFloat(individualCalibrationData.range || "");
    return sensorTypes.allSensorTypes.find((sensorType) => {
      if (
        sensorType.type !== individualCalibrationData.sensorType ||
        sensorType.category !== individualCalibrationData.category ||
        sensorType.model !== individualCalibrationData.model ||
        sensorType.measurement_range !== range
      ) {
        return false;
      }

      return (
        !isDisplacementCategory(individualCalibrationData.category) ||
        !sensorTypes.familySelectionRequired ||
        rowBelongsToFamily(sensorType, individualCalibrationData.modelFamily)
      );
    });
  }, [individualCalibrationData, sensorTypes.allSensorTypes, sensorTypes.familySelectionRequired]);

  // Step 1 gate: every filter field that applies to the current selection
  // must be filled — displacement sensors additionally require a model
  // family, everything else skips that check.
  const hasBulkSensorFilters = Boolean(
    individualCalibrationData.sensorType &&
      individualCalibrationData.category &&
      individualCalibrationData.model &&
      individualCalibrationData.range &&
      selectedBulkSensorType?.id &&
      (!isDisplacementCategory(individualCalibrationData.category) ||
        !sensorTypes.familySelectionRequired ||
        individualCalibrationData.modelFamily)
  );

  const sensorTemplate = useMemo(() => {
    if (!individualCalibrationData?.range || !schemaConfig.fieldConfig?.generateTemplateLabels) {
      return null;
    }
    const templateLabels = schemaConfig.fieldConfig.generateTemplateLabels(individualCalibrationData.range);
    if (!templateLabels) return null;

    const frequencyLines = templateLabels.frequencyLabels.map((label: string) => `${label}\t`);
    const temperatureLines = templateLabels.temperatureLabels.map((label: string) => `${label}\t`);
    return {
      frequencyText: frequencyLines.join("\n"),
      temperatureText: temperatureLines.join("\n"),
    };
  }, [individualCalibrationData.range, schemaConfig.fieldConfig]);

  const handleGenerateSampleCsv = () => {
    if (!hasBulkSensorFilters) {
      toast.error("Please select all sensor filters before generating a sample CSV.");
      return;
    }

    const csvContent = buildGeneratedSampleCsv({
      frequencyText: sensorTemplate?.frequencyText || "",
      temperatureText: sensorTemplate?.temperatureText || "",
      serialCount: 5,
    });

    downloadCsvTemplate(csvContent, "calibration-sample-generated.csv");
    toast.success("Sample CSV downloaded from the selected sensor filters.");
  };

  const isUploadStepValid = Boolean(
    bulkUpload.selectedFiles.length > 0 && bulkUpload.equipmentCalibrationCertificateNo.trim()
  );

  const handleNextFromSensorFilters = () => {
    if (!hasBulkSensorFilters) {
      toast.error("Please select all sensor filters before continuing.");
      return;
    }
    setActiveStep(1);
  };

  const handleNextFromUpload = () => {
    if (!bulkUpload.selectedFiles.length) {
      toast.error("Please upload a CSV file before continuing.");
      return;
    }
    if (!bulkUpload.equipmentCalibrationCertificateNo.trim()) {
      toast.error("Please enter an Equipment Calibration Certificate Number before continuing.");
      return;
    }
    setActiveStep(2);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleCancel = () => {
    navigate("/calibrations");
  };

  const handleProcessBulkCalibration = async () => {
    if (!hasBulkSensorFilters) {
      toast.error("Please select all sensor filters before processing.");
      setActiveStep(0);
      return;
    }
    if (!isUploadStepValid) {
      toast.error("Please upload a CSV file and enter the certificate number before processing.");
      setActiveStep(1);
      return;
    }
    if (!selectedBulkSensorType?.id) {
      toast.error("The selected sensor filters could not be resolved.");
      return;
    }

    setProcessError(null);
    const loadingToast = toast.loading("Sending bulk file for processing...");
    try {
      const certificateNo = bulkUpload.equipmentCalibrationCertificateNo.trim();
      const sharedInputData = matchesCategory(individualCalibrationData.category, "piezometer")
        ? { dwt_traceable_certificate_no: certificateNo }
        : isDisplacementCategory(individualCalibrationData.category)
        ? { equipment_calibration_certificate_no: certificateNo }
        : {};

      await CalibrationService.bulkCalibrateSensors({
        file: bulkUpload.selectedFiles[0],
        sensor_type_id: selectedBulkSensorType.id,
        on_existing_serial: "recalibrate",
        shared_input_data: JSON.stringify(sharedInputData),
      });
      toast.dismiss(loadingToast);
      toast.success(`Bulk file "${bulkUpload.selectedFiles[0].name}" is queued for processing.`);
      navigate("/calibrations");
    } catch (error: any) {
      toast.dismiss(loadingToast);
      const message = getErrorMessage(error);
      toast.error("Failed to process bulk calibration", { duration: 8000 });
      setProcessError(message);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4">
        <button
          onClick={handleCancel}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Calibrations Management"
        >
          Calibrations Management
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-primary-600 font-medium">Start Bulk Calibration</span>
      </div>

      {/* Page Header */}
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Start Bulk Calibration</h1>
          <p className="mt-1 text-sm text-gray-500">
            Choose sensor filters, upload a CSV of readings, then review before submitting
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="card p-4 sm:p-6">
        <ol className="mx-auto flex items-center justify-center">
          {STEPS.map((label, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            return (
              <li key={label} className="flex items-center">
                <div className="flex flex-shrink-0 items-center gap-2.5">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      isCompleted
                        ? "bg-primary-600 text-white"
                        : isActive
                        ? "border-2 border-primary-600 text-primary-600"
                        : "border-2 border-gray-300 text-gray-400"
                    }`}
                  >
                    {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  <span
                    className={`whitespace-nowrap text-sm font-medium ${
                      isActive || isCompleted ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`mx-6 h-0.5 w-24 sm:w-40 ${isCompleted ? "bg-primary-600" : "bg-gray-200"}`}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Process error */}
      {processError && (
        <div ref={errorBannerRef} className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
          <div className="flex flex-1 min-w-0 items-center gap-2">
            <p className="text-sm font-medium text-red-800">Bulk calibration failed</p>
            <button
              onClick={() => setIsErrorModalOpen(true)}
              className="flex-shrink-0 text-sm text-red-500 hover:text-red-700"
              title="View error details"
            >
            Detailed Error
            </button>
          </div>
          <button
            onClick={() => setProcessError(null)}
            className="flex-shrink-0 text-red-500 hover:text-red-700"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="Bulk calibration failed"
        size="lg"
      >
        <p className="text-sm text-red-700 whitespace-pre-line break-words">{processError}</p>
      </Modal>

      {/* Step content */}
      <div className="card p-4 sm:p-6 space-y-6">
        {activeStep === 0 ? (
          <SensorFiltersStep
            individualCalibrationData={individualCalibrationData}
            setIndividualCalibrationData={setIndividualCalibrationData}
            sensorTypes={sensorTypes}
            canGenerateSampleCsv={hasBulkSensorFilters}
            onGenerateSampleCsv={handleGenerateSampleCsv}
          />
        ) : activeStep === 1 ? (
          <UploadCsvStep bulkUpload={bulkUpload} />
        ) : (
          <BulkPreviewStep individualCalibrationData={individualCalibrationData} bulkUpload={bulkUpload} />
        )}

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <div>
            {activeStep > 0 && (
              <button onClick={handleBack} className="btn-secondary">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button onClick={handleCancel} className="btn-secondary">
              Cancel
            </button>
            {activeStep === 0 ? (
              <button
                onClick={handleNextFromSensorFilters}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                disabled={!hasBulkSensorFilters}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            ) : activeStep === 1 ? (
              <button
                onClick={handleNextFromUpload}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                disabled={!isUploadStepValid}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleProcessBulkCalibration}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                disabled={!hasBulkSensorFilters || !isUploadStepValid}
              >
                Send file for processing
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkCalibrationPage;
